import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AlertComponent } from '@frontendlib/components/components/alert/alert.component';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { LinkComponent } from '@frontendlib/components/components/link/link.component';
import { EventRsvpButtonsComponent } from '@modules/calendar-app/components/event-rsvp-buttons/event-rsvp-buttons.component';
import { resolveRsvpLifecycle } from '@modules/calendar-app/utils/event-rsvp.util';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { InvitationAcceptStatus } from '@sharedlib/common/types/invitation-accept-status';
import { SignalStitchEventType, SignalType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { CalendarEventDetailsDto, EventParticipantDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { combineLatest, EMPTY, merge, type Observable } from 'rxjs';
import { filter, finalize, switchMap, take, tap } from 'rxjs/operators';

/**
 * Rendering decision for the invitation card. Mutually exclusive states:
 * - `live`: REQUEST resolved and the event is still live → show RSVP buttons.
 * - `cancelled`: organizer cancelled the event (inbound CANCEL stamped
 *   `event.status='cancelled'`) — show "This event has been cancelled" alert.
 * - `trashed`: user moved the event to trash locally (`event.deleted=true`,
 *   `status` NULL) — show "This event was moved to trash" alert.
 * - `archived`: user archived the event (`event.archived=true`, not deleted,
 *   not cancelled) — show "This event was archived" alert.
 * - `fetching`: initial fetch in progress.
 * - `hidden`: nothing to render (no remote uid, non-REQUEST method, or no event stored yet — the
 *   card then waits for the event's CREATED signal).
 *
 * Priority cancelled > trashed > archived > live keeps the card honest when
 * a user trashes an already-cancelled event: the cancellation reason wins
 * because it's the upstream cause (SBUX-729).
 */
type CardState =
  | { kind: 'live'; event: CalendarEventDetailsDto }
  | { kind: 'cancelled'; event: CalendarEventDetailsDto }
  | { kind: 'trashed'; event: CalendarEventDetailsDto }
  | { kind: 'archived'; event: CalendarEventDetailsDto }
  | { kind: 'fetching' }
  | { kind: 'hidden' };

@Component({
  selector: 'app-mail-invitation-card',
  templateUrl: './mail-invitation-card.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    IconComponent,
    Button2Component,
    LinkComponent,
    DateFormatPipe,
    AlertComponent,
    EventRsvpButtonsComponent,
  ],
  host: { class: 'block' },
})
export class MailInvitationCardComponent {
  /** The ICS VEVENT UID extracted from the inbound email. Null means no card shown. */
  /** The receiving message owns the invitation, regardless of Calendar's selected area. */
  public readonly mailboxTenantId = input<string | undefined>();

  public readonly eventRemoteUid = input<string | null>(null);

  /**
   * The ICS METHOD of the inbound invitation. Only `REQUEST` carries a live
   * invitation that warrants showing the card; `CANCEL` and `REPLY` (and a
   * missing method on legacy / non-invitation messages) must hide the card
   * entirely — otherwise the card would retry its read and wait for a CREATED
   * signal for an event the cancellation correctly trashed.
   */
  public readonly eventMethod = input<'REQUEST' | 'CANCEL' | 'REPLY' | null>(null);

  private readonly eventsService = inject(EventsService);
  private readonly signalsService = inject(SignalsService);
  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly dynamicPanelService = inject(DynamicPanelService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * The account (display) zone, surfaced so the template can pass it as a pipe ARGUMENT.
   *
   * `DateFormatPipe` is pure. Reading the zone only inside `DateFormatService` is not enough: the
   * cached result would survive `DisplayTimeZoneSyncService` resolving the account zone (~180ms after
   * load, so a card rendered before that keeps the browser zone forever) and would ignore a later
   * timezone change in settings. Passing it in makes the binding change, so the pipe re-runs.
   */
  protected readonly displayTimeZone = this.displayTimeZoneService.timeZone;

  /** The resolved calendar event (null while loading or if not found). */
  protected readonly event = signal<CalendarEventDetailsDto | null>(null);

  /** True while initial event fetch is in progress. */
  protected readonly fetching = signal(false);

  /** True while the calendar event is being opened in the dynamic panel. */
  protected readonly openingEvent = signal(false);

  /** Expose enum for template (participant status chips). */
  protected readonly InvitationAcceptStatus = InvitationAcceptStatus;

  /**
   * Single source of truth for what the template renders. Computed from
   * inputs + the resolved event so trash/cancel/archive transitions
   * recompute reactively when `Event.UPDATED` signals refetch the event
   * (see `listenForEventUpdates`). Self-healing: when an inbound CANCEL
   * trashes the event server-side, the refetch returns
   * `{ deleted: true, status: 'cancelled' }`, this signal flips to
   * `cancelled`, and the template re-renders the alert without the user
   * needing to reopen the message.
   *
   * Priority cancelled > trashed > archived > live, per SBUX-729 spec.
   */
  protected readonly cardState = computed<CardState>(() => {
    if (!this.eventRemoteUid() || this.eventMethod() !== 'REQUEST') {
      return { kind: 'hidden' };
    }
    if (this.fetching()) {
      return { kind: 'fetching' };
    }
    const e = this.event();
    if (!e) {
      return { kind: 'hidden' };
    }
    // The cancelled/trashed/archived/live decision is shared with the event full form
    // (`resolveRsvpLifecycle`) so the two RSVP surfaces can never disagree about whether an
    // invitation is still answerable — they did, and the form let users RSVP to cancelled and
    // trashed events (SBUX-729 C-7/C-8). Only the card's own wrapper states live here.
    return { kind: resolveRsvpLifecycle(e), event: e };
  });

  /** Participants shown as chips. */
  protected readonly visibleParticipants = computed<EventParticipantDto[]>(() => {
    const ev = this.event();
    return ev?.participants ?? [];
  });

  /**
   * Event surfaced for the shared event-context template (title, time, link,
   * participants, Open Event button). Non-null whenever the card has a
   * resolved event to render — i.e. `live` / `cancelled` / `trashed` /
   * `archived`. Lets the template reuse a single `<ng-template>` across
   * every case branch without an `any`-typed template parameter.
   */
  protected readonly activeEvent = computed<CalendarEventDetailsDto | null>(() => {
    const state = this.cardState();
    return state.kind === 'live' || state.kind === 'cancelled' || state.kind === 'trashed' || state.kind === 'archived'
      ? state.event
      : null;
  });

  constructor() {
    // When eventRemoteUid / eventMethod changes: resolve the event only for
    // REQUEST invitations — the read (with its bounded 404 retry) races the
    // event's CREATED signal, whichever finds it first wins — then listen for
    // real-time update signals to refresh the card. CANCEL / REPLY / null
    // method short-circuit — there is no event to fetch (CANCEL trashed it)
    // and the card must stay hidden. Nested switchMap auto-disposes the
    // in-flight read, the CREATED wait AND the previous update subscription
    // when inputs change — no manual Subscription bookkeeping.
    combineLatest([
      toObservable(this.eventRemoteUid),
      toObservable(this.eventMethod),
      toObservable(this.mailboxTenantId),
    ])
      .pipe(
        tap(() => {
          this.event.set(null);
        }),
        switchMap(([uid, method]) => {
          if (uid === null || uid.length === 0 || method !== 'REQUEST') {
            return EMPTY;
          }
          this.fetching.set(true);
          return merge(this.fetchWithRetry(uid), this.fetchOnCreated(uid)).pipe(
            filter((eventDto): eventDto is CalendarEventDetailsDto => eventDto !== null),
            take(1),
            tap((eventDto) => {
              this.fetching.set(false);
              this.event.set(eventDto);
            }),
            switchMap((eventDto) => this.listenForEventUpdates(eventDto.id, uid)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected openEventInCalendar(): void {
    const ev = this.event();
    if (!ev?.id || this.openingEvent()) {
      return;
    }

    this.openingEvent.set(true);
    const requestContext = owningTenantContext(this.mailboxTenantId());
    const item$ = requestContext
      ? this.eventsService.getItem(ev.id, { requestContext })
      : this.eventsService.getItem(ev.id);
    item$
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.openingEvent.set(false)),
      )
      .subscribe({
        next: (event) => {
          this.dynamicPanelService.setFormItem(event);
        },
        error: () => {
          this.toasterService.show({ text: 'Failed to open event. Please try again.' });
        },
      });
  }

  /**
   * The mail is announced in the same `Promise.all` that queues the invitation for calendar-app, so
   * the first read may run before the event is stored: a 404 is read again for a bounded window.
   * Null once the window runs out — the card is then hidden (not "loading") while it keeps waiting
   * for the CREATED signal.
   */
  private ownerConfig() {
    const requestContext = owningTenantContext(this.mailboxTenantId());
    return requestContext ? { requestContext } : {};
  }

  private fetchEvent(remoteUid: string): Observable<CalendarEventDetailsDto | null> {
    return this.mailboxTenantId()
      ? this.eventsService.fetchByRemoteUid(remoteUid, this.ownerConfig())
      : this.eventsService.fetchByRemoteUid(remoteUid);
  }

  private fetchWithRetry(remoteUid: string): Observable<CalendarEventDetailsDto | null> {
    return this.eventsService
      .fetchByRemoteUid(remoteUid, { waitForCreation: true, ...this.ownerConfig() })
      .pipe(tap(() => this.fetching.set(false)));
  }

  /**
   * calendar-app announces the stored event with a CREATED signal carrying its summary, and
   * `remoteUid` is the only key the card knows before the event exists. One read follows, with no
   * retry: calendar-app reads the primary, so the row it just committed is there. Subscribed before
   * the first read goes out, so a signal emitted during that read's round trip is not lost.
   */
  private fetchOnCreated(remoteUid: string): Observable<CalendarEventDetailsDto | null> {
    return this.stitchEventsService
      .getEvents(
        this.mailboxTenantId() ?? this.spaceContextStore.contextId(),
        StitchType.EVENT,
        SignalStitchEventType.CREATED,
      )
      .pipe(
        filter((signal) => signal.items.some((item) => item.remoteUid === remoteUid)),
        take(1),
        switchMap(() => this.fetchEvent(remoteUid)),
      );
  }

  /**
   * Real-time event update stream for a given event UID. Returned as an inner
   * observable so the outer UID pipeline can dispose it via `switchMap` when a
   * new UID arrives — no manual subscription cleanup required.
   */
  private listenForEventUpdates(eventUid: string, remoteUid: string): Observable<CalendarEventDetailsDto | null> {
    return this.signalsService
      .getSignal(SignalType.STITCH_EVENT, this.mailboxTenantId() ?? this.spaceContextStore.contextId())
      .pipe(
        filter((signal) => signal.stitchType === StitchType.EVENT && signal.ids?.includes(eventUid)),
        switchMap(() => this.fetchEvent(remoteUid)),
        tap((eventDto) => {
          if (eventDto) {
            this.event.set(eventDto);
          }
        }),
      );
  }
}
